/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import {
  BlockType as PrismaBlockType,
  FileStatus as PrismaFileStatus,
  ImageFit as PrismaImageFit,
} from "@vienna/prisma";

import {
  BaseBlockSnapshot,
  BlockSnapshot,
  BlockType,
  BlockWindowSnapshot,
  CodeBlockSnapshot,
  ColorBlockSnapshot,
  FileBlockSnapshot,
  FileStatus,
  ImageBlockSnapshot,
  ImageFit,
  ImageTag,
  ImageTagType,
  LinkBlockSnapshot,
  LinkEmbedType,
  PlateWindowSnapshot,
  ProgrammingLanguage,
  TextBlockSnapshot,
  TextType,
} from "../entities/block-snapshot.entity";
import { Block, EmbedBlock, FileBlock, ImageBlock, WebEntity } from "../entities/block.entity";

import { PlateConverter } from "./PlateConverter";
// @todo - type assertions everywhere. what is actually required and when?
export class BlockConverter {
  /* -------------------------------------------------- */
  /*                    To Snapshot                    */
  /* -------------------------------------------------- */
  public static toSnapshot(backendBlock: Block, currentUserId: string | null): BlockSnapshot {
    const baseSnapshot: BaseBlockSnapshot = {
      id: backendBlock.id,
      top: backendBlock.top!,
      left: backendBlock.left!,
      width: backendBlock.width!,
      height: backendBlock.height!,
      zIndex: backendBlock.zIndex || 0,
      plateId: backendBlock.pageId!,
      createdAt: backendBlock.createdAt.toString(),
      isLiked: backendBlock.isLiked || false,
      connectionCount: backendBlock.numberOfEmbeds || 0,
      currentUserIsOwner: currentUserId ? backendBlock.userId === currentUserId : false,
      userId: backendBlock.userId,
    };

    switch (backendBlock.type) {
      case "text":
        const textSnapshot: TextBlockSnapshot = {
          ...baseSnapshot,
          type: BlockType.Text,
          textContent: backendBlock.textContent || [],
          rawContent: backendBlock.rawContent || [],
          plainText: backendBlock.plainText || "",
          textType: (backendBlock.textSubtype as TextType) || TextType.Plain,
        };
        return textSnapshot;
      case "image":
        const imageSnapshot: ImageBlockSnapshot = {
          ...baseSnapshot,
          type: BlockType.Image,
          imageFit: backendBlock.imageFit === PrismaImageFit.contain ? ImageFit.Contain : ImageFit.Cover,
          filename: backendBlock.filename!,
          filesize: backendBlock.fileSize!,
          originalFilename: backendBlock.originalFilename!,
          tags: this.getImageTags(backendBlock),
          caption: backendBlock.caption || null,
          hasCaption: !!backendBlock.caption,
          fileStatus: this.toSnapshotFileStatus(backendBlock),
          uploadPercent: backendBlock.uploadPercent || 0,
        };
        return imageSnapshot;
      case "color":
        const colorSnapshot: ColorBlockSnapshot = {
          ...baseSnapshot,
          type: BlockType.Color,
          hex: backendBlock.hex!,
        };
        return colorSnapshot;
      case "code":
        const codeSnapshot: CodeBlockSnapshot = {
          ...baseSnapshot,
          type: BlockType.Code,
          plainText: backendBlock.plainText || "",
          language: (backendBlock.codeLanguage as ProgrammingLanguage) || ProgrammingLanguage.Javascript,
        };
        return codeSnapshot;
      case "linkEmbed":
      case "spotifyEmbed":
      case "twitterEmbed":
      case "youtubeEmbed":
      case "instagramEmbed":
        const linkSnapshot: LinkBlockSnapshot = {
          ...baseSnapshot,
          type: BlockType.Link,
          linkEmbedType: this.getSnapshotLinkType(backendBlock),
          url: backendBlock.plainText!,
        };
        return linkSnapshot;
      case "file":
        const fileSnapshot: FileBlockSnapshot = {
          ...baseSnapshot,
          type: BlockType.File,
          filename: backendBlock.filename || null,
          filesize: backendBlock.fileSize || null,
          originalFilename: backendBlock.originalFilename || null,
          fileStatus: this.toSnapshotFileStatus(backendBlock),
          uploadPercent: backendBlock.uploadPercent!,
        };
        return fileSnapshot;
      case "blockWindow":
        const initialBlockSnapshot = backendBlock.embeddedBlock
          ? this.convertEmbeddedBlock(backendBlock.embeddedBlock, currentUserId)
          : null;
        const blockWindowSnapshot: BlockWindowSnapshot = {
          ...baseSnapshot,
          isLiked: initialBlockSnapshot?.isLiked || false, // note this is purely for the UI
          type: BlockType.BlockWindow,
          initialBlockSnapshot,
        };
        return blockWindowSnapshot;
      case "pageWindow":
        const initialPlateSnapshot = backendBlock.embeddedPage
          ? PlateConverter.toBaseSnapshot(backendBlock.embeddedPage, currentUserId)
          : null;
        const plateWindowSnapshot: PlateWindowSnapshot = {
          ...baseSnapshot,
          isLiked: initialPlateSnapshot?.isLiked || false, // note this is purely for the UI
          type: BlockType.PlateWindow,
          initialPlateSnapshot,
        };
        return plateWindowSnapshot;
      default:
        throw new Error("Unknown block type");
    }
  }

  /* -------------------------------------------------- */
  /*                     To Prisma                      */
  /* -------------------------------------------------- */

  public static toPrisma(block: Partial<BlockSnapshot>): Block {
    const prismaBlock: any = {
      id: block.id,
      type: block.type,
      top: block.top,
      left: block.left,
      width: block.width,
      height: block.height,
      zIndex: block.zIndex,
      pageId: block.plateId,
      createdAt: new Date(block.createdAt || ""),
      userId: block.userId,
    };
    switch (block.type) {
      case BlockType.Text: {
        const textBlock = block as TextBlockSnapshot;
        prismaBlock.textContent = textBlock.textContent;
        prismaBlock.plainText = textBlock.plainText;
        prismaBlock.textSubtype = textBlock.textType;
        break;
      }
      case BlockType.Code: {
        const codeBlock = block as CodeBlockSnapshot;
        prismaBlock.plainText = codeBlock.plainText;
        prismaBlock.codeLanguage = codeBlock.language;
        break;
      }
      case BlockType.Image: {
        const imageBlock = block as ImageBlockSnapshot;
        prismaBlock.filename = imageBlock.filename;
        prismaBlock.fileSize = imageBlock.filesize;
        prismaBlock.originalFilename = imageBlock.originalFilename;
        prismaBlock.imageFit = imageBlock.imageFit.toLowerCase();
        prismaBlock.caption = imageBlock.caption;
        prismaBlock.fileStatus = this.toPrismaFileStatus(imageBlock.fileStatus);
        break;
      }
      case BlockType.Link: {
        const linkBlock = block as LinkBlockSnapshot;
        prismaBlock.plainText = linkBlock.url;
        prismaBlock.type = this.getLinkEmbedType(linkBlock.linkEmbedType);
        break;
      }
      case BlockType.Color: {
        const colorBlock = block as ColorBlockSnapshot;
        prismaBlock.hex = colorBlock.hex;
        break;
      }
      case BlockType.File: {
        const fileBlock = block as FileBlockSnapshot;
        prismaBlock.filename = fileBlock.filename;
        prismaBlock.fileSize = fileBlock.filesize;
        prismaBlock.originalFilename = fileBlock.originalFilename;
        prismaBlock.fileStatus = this.toPrismaFileStatus(fileBlock.fileStatus);
        break;
      }
      case BlockType.BlockWindow: {
        const blockWindowBlock = block as BlockWindowSnapshot;
        prismaBlock.embeddedBlockId = blockWindowBlock.initialBlockSnapshot?.id;
        break;
      }
      case BlockType.PlateWindow: {
        const plateWindowBlock = block as PlateWindowSnapshot;
        prismaBlock.embeddedPageId = plateWindowBlock.initialPlateSnapshot?.id;
        break;
      }
    }
    return prismaBlock as unknown as Block;
  }

  //* -------------------------------------------------- */
  /*                      Helpers                       */
  /* -------------------------------------------------- */

  private static getImageTags(backendBlock: ImageBlock): ImageTag[] {
    const customTags: ImageTag[] =
      backendBlock.customTags?.map((customTag) => ({ tag: customTag, type: ImageTagType.Custom })) || [];

    const generatedTags: ImageTag[] =
      (backendBlock.webDetection?.webEntities as WebEntity[])?.map((entity) => ({
        tag: entity.description,
        type: ImageTagType.Generated,
      })) || [];
    return [...customTags, ...generatedTags];
  }

  private static getLinkEmbedType(embedType: LinkEmbedType): PrismaBlockType {
    switch (embedType) {
      case LinkEmbedType.Youtube:
        return "youtubeEmbed";
      case LinkEmbedType.Twitter:
        return "twitterEmbed";
      case LinkEmbedType.Instagram:
        return "instagramEmbed";
      case LinkEmbedType.Spotify:
        return "spotifyEmbed";
      case LinkEmbedType.Other:
        return "linkEmbed";
    }
  }

  private static getSnapshotLinkType(backendBlock: EmbedBlock): LinkEmbedType {
    switch (backendBlock.type) {
      case "linkEmbed":
        return LinkEmbedType.Other;
      case "spotifyEmbed":
        return LinkEmbedType.Spotify;
      case "twitterEmbed":
        return LinkEmbedType.Twitter;
      case "youtubeEmbed":
        return LinkEmbedType.Youtube;
      case "instagramEmbed":
        return LinkEmbedType.Instagram;
      default:
        return LinkEmbedType.Other;
    }
  }

  private static toSnapshotFileStatus(backendBlock: ImageBlock | FileBlock): FileStatus {
    if (!backendBlock.filename) return FileStatus.None; // prisma file status enum has only "UPLOADED", "UPLOADING", "FAILED". It defaults to UPLOADED. Which is dumb.
    switch (backendBlock.fileStatus) {
      case "UPLOADED":
        return FileStatus.Uploaded;
      case "UPLOADING":
        return FileStatus.Uploading;
      case "FAILED":
        return FileStatus.Failed;
      default:
        return FileStatus.None;
    }
  }

  private static toPrismaFileStatus(fileStatus: FileStatus): PrismaFileStatus | null {
    switch (fileStatus) {
      case FileStatus.Uploaded:
        return "UPLOADED";
      case FileStatus.Uploading:
        return "UPLOADING";
      case FileStatus.Failed:
        return "FAILED";
      case FileStatus.None:
        return null;
    }
  }

  private static convertEmbeddedBlock(
    block: Block,
    currentUserId: string | null
  ): Exclude<BlockSnapshot, BlockWindowSnapshot | PlateWindowSnapshot> {
    if (block.type === BlockType.BlockWindow || block.type === BlockType.PlateWindow) {
      throw new Error("Cannot convert embedded block");
    }
    return this.toSnapshot(block, currentUserId) as Exclude<
      BlockSnapshot,
      BlockWindowSnapshot | PlateWindowSnapshot
    >;
  }
}
